import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, ModalityBadge, modColor } from "@/components/Shared";
import { Users, Scan, ScanLine, Layers, AlertTriangle, Clock, ArrowLeftRight } from "lucide-react";

function StatCard({ icon: Icon, label, value, accent, testid }) {
  return (
    <div
      data-testid={testid}
      className="bg-[#0F1219] border border-slate-800 hover:border-[#D902EE]/40 p-5 transition-all duration-300 group"
    >
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs tracking-[0.2em] uppercase text-slate-400">{label}</span>
        <Icon className="w-4 h-4" style={{ color: accent }} />
      </div>
      <div className="font-mono-rs text-4xl font-bold" style={{ color: accent }}>{value}</div>
    </div>
  );
}

function ModalityPanel({ name, data, accent }) {
  const staffed = data.assigned;
  const total = data.total;
  return (
    <div className="bg-[#0F1219] border border-slate-800 p-5" data-testid={`modality-panel-${name}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <ModalityBadge modality={name} />
          <span className="text-sm text-slate-300">Today's Coverage</span>
        </div>
        <span className="font-mono-rs text-sm" style={{ color: accent }}>{staffed}/{total} staffed</span>
      </div>
      {total === 0 ? (
        <p className="text-sm text-slate-500">No {name} shifts scheduled today.</p>
      ) : (
        <div className="space-y-2">
          {data.shifts.map((s) => (
            <div key={s.id} className="flex items-center justify-between border-l-2 pl-3 py-2 bg-[#05060A]"
                 style={{ borderColor: accent }}>
              <div>
                <div className="font-mono-rs text-sm text-white">{s.start_time}–{s.end_time}</div>
              </div>
              <div className="text-right">
                {s.tech_name ? (
                  <span className="text-sm text-slate-200">{s.tech_name}</span>
                ) : (
                  <span className="text-xs font-mono-rs uppercase tracking-wider text-[#FFB800]">Unstaffed</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminDashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return <div className="font-mono-rs text-[#00F0FF] tracking-widest animate-pulse">LOADING…</div>;

  const s = data.stats;
  return (
    <div>
      <PageHeader title="Department Overview" subtitle={`// Live status — ${data.date}`} />

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <StatCard testid="stat-total-techs" icon={Users} label="Active Techs" value={s.total_techs} accent="#F8FAFC" />
        <StatCard testid="stat-ct-techs" icon={Scan} label="CT Techs" value={s.ct_techs} accent="#D902EE" />
        <StatCard testid="stat-xray-techs" icon={ScanLine} label="X-ray Techs" value={s.xray_techs} accent="#00F0FF" />
        <StatCard testid="stat-both-techs" icon={Layers} label="Dual-Cert" value={s.both_techs} accent="#B15CFF" />
        <StatCard testid="stat-conflicts" icon={AlertTriangle} label="Conflicts (14d)" value={s.conflicts} accent={s.conflicts ? "#FF003C" : "#00FF66"} />
      </div>

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4 mb-6">
        {Object.entries(data.per_modality).map(([name, d]) => (
          <ModalityPanel key={name} name={name} data={d} accent={modColor(name)} />
        ))}
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <div className="md:col-span-2 bg-[#0F1219] border border-[#FFB800]/30 p-5" data-testid="understaffed-panel">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="w-4 h-4 text-[#FFB800]" />
            <h3 className="font-mono-rs text-sm tracking-[0.2em] uppercase text-[#FFB800]">Understaffed Today</h3>
          </div>
          {data.understaffed.length === 0 ? (
            <p className="text-sm text-[#00FF66]">All shifts fully staffed for today.</p>
          ) : (
            <div className="space-y-2">
              {data.understaffed.map((s2) => (
                <div key={s2.id} className="flex items-center justify-between border border-[#FFB800]/20 px-3 py-2">
                  <div className="flex items-center gap-3">
                    <ModalityBadge modality={s2.modality} />
                    <span className="font-mono-rs text-sm">{s2.start_time}–{s2.end_time}</span>
                  </div>
                  <span className="text-xs font-mono-rs uppercase text-[#FFB800]">Needs Tech</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-[#0F1219] border border-slate-800 p-5 space-y-4" data-testid="pending-summary">
          <h3 className="font-mono-rs text-sm tracking-[0.2em] uppercase text-slate-300">Pending Requests</h3>
          <div className="flex items-center justify-between border-l-2 border-[#00F0FF] pl-3 py-2 bg-[#05060A]">
            <div className="flex items-center gap-2 text-slate-300 text-sm"><Clock className="w-4 h-4 text-[#00F0FF]" /> Time-off</div>
            <span className="font-mono-rs text-2xl text-[#00F0FF]">{s.pending_timeoff}</span>
          </div>
          <div className="flex items-center justify-between border-l-2 border-[#D902EE] pl-3 py-2 bg-[#05060A]">
            <div className="flex items-center gap-2 text-slate-300 text-sm"><ArrowLeftRight className="w-4 h-4 text-[#D902EE]" /> Swaps</div>
            <span className="font-mono-rs text-2xl text-[#D902EE]">{s.pending_swaps}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
